import {
  getNearestSelectedAnchorId,
  getRangeIdsFromAnchor,
} from '../../services/shift-range-selection.util';

export class DatatableRangeSelectionController<T extends string | number> {
  private lastRangeSelectionId?: T;
  private lastRangeDeselectionId?: T;

  resetAnchors() {
    this.lastRangeSelectionId = undefined;
    this.lastRangeDeselectionId = undefined;
  }

  handleToggle(
    selectionId: T,
    shiftKey: boolean,
    checked: boolean,
    idsInOrder: T[],
    selectedIds: T[],
  ): { selectIds: T[]; deselectIds: T[] } {
    const selectedIdsBeforeToggle = this.getSelectedIdsBeforeToggle(
      selectedIds,
      selectionId,
      checked,
    );

    if (shiftKey && !checked && this.lastRangeDeselectionId == null) {
      this.lastRangeDeselectionId = selectionId;
      if (selectionId === this.lastRangeSelectionId) {
        this.lastRangeSelectionId = this.getNearestSelectedAnchor(
          selectionId,
          idsInOrder,
          selectedIds,
        );
      }
      return { selectIds: [], deselectIds: [] };
    }

    const previousId = this.getPreviousRangeAnchor(
      selectionId,
      shiftKey,
      checked,
      idsInOrder,
      selectedIdsBeforeToggle,
    );

    let selectIds: T[] = [];
    let deselectIds: T[] = [];
    if (shiftKey && previousId != null && previousId !== selectionId) {
      const rangeIds = getRangeIdsFromAnchor(idsInOrder, previousId, selectionId, new Set());
      if (checked) selectIds = rangeIds;
      else deselectIds = rangeIds;
    }

    this.updateAnchorsAfterToggle(selectionId, checked, shiftKey, idsInOrder, selectedIds);
    return { selectIds, deselectIds };
  }

  private getPreviousRangeAnchor(
    selectionId: T,
    shiftKey: boolean,
    checked: boolean,
    idsInOrder: T[],
    selectedIdsBeforeToggle: T[],
  ): T | undefined {
    if (!shiftKey) return this.lastRangeSelectionId;
    if (!checked) return this.lastRangeDeselectionId;
    return this.getShiftRangeAnchor(
      selectionId,
      idsInOrder,
      this.lastRangeSelectionId,
      selectedIdsBeforeToggle,
    );
  }

  private getShiftRangeAnchor(
    selectionId: T,
    idsInOrder: T[],
    fallbackAnchor: T | undefined,
    selectedIds: T[],
  ): T | undefined {
    const clickedIndex = idsInOrder.indexOf(selectionId);
    if (clickedIndex === -1) return fallbackAnchor;

    const selectedBounds = this.getSelectedBounds(idsInOrder, selectedIds);
    if (!selectedBounds) return fallbackAnchor;

    const { minIndex, maxIndex } = selectedBounds;
    if (clickedIndex < minIndex) return idsInOrder[maxIndex];
    if (clickedIndex > maxIndex) return idsInOrder[minIndex];
    return fallbackAnchor ?? idsInOrder[minIndex];
  }

  private getSelectedBounds(
    idsInOrder: T[],
    selectedIds: T[],
  ): { minIndex: number; maxIndex: number } | undefined {
    let minIndex = Number.POSITIVE_INFINITY;
    let maxIndex = Number.NEGATIVE_INFINITY;
    for (const id of selectedIds) {
      const idx = idsInOrder.indexOf(id);
      if (idx === -1) continue;
      if (idx < minIndex) minIndex = idx;
      if (idx > maxIndex) maxIndex = idx;
    }
    if (minIndex === Number.POSITIVE_INFINITY || maxIndex === Number.NEGATIVE_INFINITY) return;
    return { minIndex, maxIndex };
  }

  private getSelectedIdsBeforeToggle(selectedIds: T[], selectionId: T, checked: boolean): T[] {
    if (checked) return selectedIds.filter((id) => id !== selectionId);
    if (selectedIds.includes(selectionId)) return selectedIds;
    return [...selectedIds, selectionId];
  }

  private getNearestSelectedAnchor(uncheckedId: T, idsInOrder: T[], selectedIds: T[]) {
    return getNearestSelectedAnchorId(idsInOrder, uncheckedId, selectedIds);
  }

  private updateAnchorsAfterToggle(
    selectionId: T,
    checked: boolean,
    shiftKey: boolean,
    idsInOrder: T[],
    selectedIds: T[],
  ) {
    if (checked) {
      this.lastRangeDeselectionId = undefined;
      if (!shiftKey || this.lastRangeSelectionId == null) {
        this.lastRangeSelectionId = selectionId;
      }
      return;
    }

    if (!shiftKey || this.lastRangeDeselectionId == null) {
      this.lastRangeDeselectionId = selectionId;
    }
    if (selectionId === this.lastRangeSelectionId) {
      this.lastRangeSelectionId = this.getNearestSelectedAnchor(
        selectionId,
        idsInOrder,
        selectedIds,
      );
    }
  }
}
